import { Component, Input, OnInit, Signal, computed } from '@angular/core';
import {
  DocumentChangeEvent,
  DocumentUploadComponent,
} from 'src/app/components/document-upload/document-upload.component';
import { CommonModule } from '@angular/common';
import { CtaButtonsComponent } from 'src/app/components/cta-buttons/cta-buttons.component';
import {
  advancedPages,
  idTypeThai,
  idTypeNonThai,
  IdType,
} from '../advanced-data';
import { ActivatedRoute } from '@angular/router';
import {
  DocumentUploadFormKeys,
  DocumentUploadFormModel,
} from 'src/app/components/document-upload/models/document-upload-form.model';
import { Validators, FormGroup, Form } from '@angular/forms';
import { UploadService } from 'src/app/services/upload.service';
import { v4 as uuidv4 } from 'uuid';
import { SharedModule } from 'src/app/shared.module';
import i18next from 'i18next';

@Component({
  selector: 'app-upload-id-th',
  standalone: true,
  imports: [
    DocumentUploadComponent,
    CommonModule,
    CtaButtonsComponent,
    SharedModule,
  ],
  templateUrl: './upload-id-th.component.html',
  styleUrl: './upload-id-th.component.scss',
})
export class UploadIdThComponent implements OnInit {
  constructor(
    private route: ActivatedRoute,
    private uploadService: UploadService
  ) {
    this.route.queryParams.subscribe((params) => {
      this.nationality = params['type'] || 'thai';
    });
  }

  public isLoading: boolean = false;
  public fileSizeLimit: number = 2 * 1024 * 1024;
  public nationality: string = 'thai';
  advancedPages: string[] = advancedPages;
  primaryIdForm = DocumentUploadFormModel.build({
    expiration: {
      validators: [],
    },
    file: {
      validators: [Validators.required],
    },
    idType: {
      validators: [Validators.required],
    },
  });

  formData: { [key: string]: DocumentChangeEvent | undefined } = {};
  nextDisabled: boolean = false;
  merchantId: number = 0;
  uploadError: string = '';
  idType: Signal<IdType> = computed(() => {
    return (
      this.idTypes.find(
        (idType) => idType.name === this.primaryIdForm.value.idType
      ) ?? {
        id: 0,
        category: '',
        name: '',
        sample: [],
        hasBackOfId: false,
        hasExpiry: false,
      }
    );
  });

  ngOnInit(): void {
    this.nextDisabled = true;
    this.merchantId = parseInt(localStorage.getItem('userId')!);

    if (this.nationality) {
      if (this.nationality == 'thai')
        this.primaryIdForm.patchValue({ idType: 'Thai ID' });
      else this.primaryIdForm.patchValue({ idType: 'Passport' });
    }

    this.primaryIdForm.get('file')?.setValidators([Validators.required]);

    this.primaryIdForm.valueChanges.subscribe((data) => {
      const selectedIdType = this.idTypes.find(
        (idType) => idType.name === this.primaryIdForm.value.idType
      );

      this.nextDisabled = false;

      if (!selectedIdType) {
        console.log('Has not selected id type');
        this.nextDisabled = true;
      }

      if (!this.primaryIdForm.value.file) {
        console.log('Has no front id');
        this.nextDisabled = true;
      }
      if (selectedIdType && selectedIdType.hasExpiry) {
        if (!this.primaryIdForm.value.expiration) {
          console.log('Has no expiration date');
          this.nextDisabled = true;
        }
      }
    });
  }

  public get idTypes() {
    if (this.nationality == 'thai') return idTypeThai;
    return idTypeNonThai;
  }

  handleUploadError(error: Error, file: File | null | undefined): boolean {
    if (file instanceof File) {
      this.uploadError = i18next.t('advanced-pages.upload-id.upload-error');
      console.error('Error uploading primary ID: ', error, file.size);
      this.isLoading = false;
      return false;
    } else {
      console.error('File is not valid:', file);
      this.isLoading = false;
      return false;
    }
  }

  async validateAndHandleLargeFile(
    file: File,
    idType: string
  ): Promise<File | null> {
    if (file.size > this.fileSizeLimit) {
      console.log('IC00 Trigger for image compression', file.size, idType);
      const compressedFile = await this.uploadService.compressImage(file);
      if (!compressedFile) {
        this.isLoading = false;
        return null;
      }
      return compressedFile;
    }
    return file;
  }

  onNextPressed = async () => {
    if(this.isLoading) {
      this.isLoading = false;
      return false;
    }
  
    this.isLoading = true;

    return await this.submitID().then((results: any) => {
      return results;
    });
  };

  uploadFile(payload: any): Promise<any> {
    return this.uploadService
      .uploadFile(
        payload.file,
        payload.merchant_id,
        payload.uid,
        payload.document_name,
        payload.status,
        payload.extras
      )
      .toPromise();
  }

  async submitID(): Promise<boolean> {
    if (!this.merchantId) {
      this.uploadError =
        'Something went wrong with the upload, please try again';
      console.log('No merchant ID on primary ID upload');
      this.isLoading = false;
      return false;
    }

    const idType = this.nationality == 'thai' ? 'thai-id' : 'passport';

    this.primaryIdForm.markAllAsTouched();
    this.primaryIdForm.controls['idType'].setValue(
      idType == 'thai-id' ? 'Thai ID' : 'Passport'
    );

    if (!this.primaryIdForm.valid) {
      this.isLoading = false;
      return false;
    }

    const file = this.primaryIdForm.value.file;

    if (!file) {
      this.uploadError = 'A file is required before proceeding';
      this.isLoading = false;
      return false;
    }

    const validatedID = await this.validateAndHandleLargeFile(file, idType);

    const payload = {
      file: validatedID,
      merchant_id: this.merchantId,
      uid: uuidv4(),
      document_name: idType,
      extras: JSON.stringify({
        id_subtype: this.primaryIdForm.value.idType,
        expiration_date: this.primaryIdForm.value.expiration,
      }),
    };

    console.log('ID Payload', payload, this.merchantId);

    return await this.uploadFile(payload)
      .then(() => {
        return true;
      })
      .catch((error) => {
        return this.handleUploadError(error, file);
      });
  }
}
